// app/javascript/components/student/ShareResultModal.tsx
import React, { useState, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Share2, MessageCircle, Link2, CheckCircle2, Send, Award, Target, Trophy, Clock, ShieldCheck, Download, Globe, Image as ImageIcon } from 'lucide-react'
import { toPng } from 'html-to-image'

interface Props {
  open: boolean
  onClose: () => void
  testTitle: string
  examName?: string
  score: number
  maxScore: number
  accuracy: number
  rankPercentile?: number | null
  attemptNumber?: number
  timeSpentSeconds?: number
  studentName?: studentName
}

export function ShareResultModal({
  open,
  onClose,
  testTitle,
  examName,
  score,
  maxScore,
  accuracy,
  rankPercentile,
  attemptNumber = 1,
  timeSpentSeconds = 0,
  studentName = 'Student',
}: Props) {
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const shareableUrl = 'https://prepkiya.com'
  const displayExamName = examName || 'Mock Exam'
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0

  const shareText = `🎓 OFFICIAL ACADEMIC ACHIEVEMENT CERTIFICATE 🏆\n\nExam: ${displayExamName}\nTest: "${testTitle}"\nScore: ${score}/${maxScore} (${pct}%)\nAccuracy: ${accuracy}%${rankPercentile ? `\nRank: Top ${100 - rankPercentile}% nationwide! 🚀` : ''}\n\nCheck out & test your knowledge on PrepKiya: ${shareableUrl}`

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Capture options for html-to-image to prevent canvas clipping
  const getCaptureOptions = (node: HTMLElement) => ({
    cacheBust: true,
    pixelRatio: 3,
    width: node.offsetWidth,
    height: node.offsetHeight,
    style: {
      margin: '0',
      transform: 'none',
    }
  })

  // Generate HD PNG blob of the card component
  const getCardImageBlob = async (): Promise<Blob | null> => {
    if (!cardRef.current) return null
    const dataUrl = await toPng(cardRef.current, getCaptureOptions(cardRef.current))
    const res = await fetch(dataUrl)
    return await res.blob()
  }

  // Download Card Image file directly
  const handleDownloadImage = async () => {
    if (!cardRef.current) return
    setDownloading(true)
    try {
      const dataUrl = await toPng(cardRef.current, getCaptureOptions(cardRef.current))
      const link = document.createElement('a')
      link.download = `PrepKiya-Certificate-${testTitle.slice(0, 20)}.png`
      link.href = dataUrl
      link.click()
    } catch (e) {
      console.error('Failed to capture certificate image', e)
    } finally {
      setDownloading(false)
    }
  }

  // Web Share API (native share with image attachment for Mobile/WhatsApp)
  const handleNativeShareImage = async () => {
    if (!cardRef.current) return
    setDownloading(true)
    try {
      const blob = await getCardImageBlob()
      if (blob && navigator.canShare && navigator.canShare({ files: [new File([blob], 'certificate.png', { type: 'image/png' })] })) {
        const file = new File([blob], 'PrepKiya-Certificate.png', { type: 'image/png' })
        await navigator.share({
          title: `PrepKiya Certificate - ${testTitle}`,
          text: shareText,
          files: [file]
        })
      } else {
        // Fallback: download image and open WhatsApp
        handleDownloadImage()
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank')
      }
    } catch (e) {
      console.error('Share error', e)
    } finally {
      setDownloading(false)
    }
  }

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableUrl)}`

  const formatMins = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[92dvh] overflow-y-auto bg-card/95 backdrop-blur-xl border-border/80 shadow-glass-lg p-4 sm:p-6 rounded-2xl">
        <DialogHeader className="pr-6">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg font-bold text-foreground">
            <Share2 className="h-5 w-5 text-primary shrink-0" /> Share Achievement Certificate
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Download your HD certificate image or share directly to social platforms.
          </DialogDescription>
        </DialogHeader>

        {/* Certificate / Digital Achievement Card (Target for HTML-to-Image capture) */}
        <div
          ref={cardRef}
          className="rounded-2xl p-4 sm:p-6 overflow-hidden border-2 border-primary/40 shadow-xl bg-card my-1 sm:my-2 space-y-3 sm:space-y-4 text-card-foreground"
          style={{
            boxSizing: 'border-box',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          }}
        >
          {/* Certificate Header */}
          <div className="flex items-center justify-between border-b border-border/60 pb-3.5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary text-primary-fg flex items-center justify-center font-extrabold text-sm shadow-xs">
                PK
              </div>
              <div>
                <p className="text-xs font-extrabold text-primary uppercase tracking-wider">
                  PrepKiya Verified
                </p>
                <p className="text-[10px] text-muted-foreground font-semibold">{displayExamName} Achievement</p>
              </div>
            </div>

            {rankPercentile != null && rankPercentile > 0 ? (
              <Badge variant="tertiary" className="text-[11px] font-extrabold uppercase tracking-wider gap-1.5 px-2.5 py-1">
                <Trophy className="h-3.5 w-3.5" /> Top {(100 - rankPercentile).toFixed(0)}%
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-[11px] font-extrabold uppercase tracking-wider gap-1.5 px-2.5 py-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Certified
              </Badge>
            )}
          </div>

          {/* Test Title & Student Info */}
          <div className="space-y-1.5">
            {examName && (
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-secondary">
                {examName}
              </span>
            )}
            <h3 className="text-lg font-extrabold text-foreground leading-snug">
              {testTitle}
            </h3>
            <p className="text-xs text-muted-foreground font-medium">
              Achieved by <strong className="text-foreground">{studentName}</strong> • Attempt #{attemptNumber}
            </p>
          </div>

          {/* KPI Stats Grid - Solid Card Surface */}
          <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-muted/60 border border-border/80 text-center">
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1">
                <Award className="h-3.5 w-3.5 text-primary" /> Score
              </p>
              <p className="text-xl font-extrabold text-primary mt-1">
                {score}<span className="text-xs text-muted-foreground">/{maxScore}</span>
              </p>
            </div>

            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1">
                <Target className="h-3.5 w-3.5 text-secondary" /> Accuracy
              </p>
              <p className="text-xl font-extrabold text-secondary mt-1">{accuracy}%</p>
            </div>

            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1">
                <Clock className="h-3.5 w-3.5 text-tertiary" /> Time
              </p>
              <p className="text-xl font-extrabold text-tertiary mt-1">{formatMins(timeSpentSeconds)}</p>
            </div>
          </div>

          {/* Verification Footer Stamp */}
          <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-3 pb-1 border-t border-border/60">
            <span className="flex items-center gap-1.5 text-secondary font-bold">
              <CheckCircle2 className="h-3.5 w-3.5" /> Verified Evaluation Score
            </span>
            <span className="font-mono font-bold text-foreground">prepkiya.com</span>
          </div>
        </div>

        {/* Action Buttons: Image Download & Direct Social Share */}
        <div className="space-y-2.5 pt-2 pb-2">
          <Button
            variant="outline"
            className="w-full h-10 gap-2 font-bold text-xs sm:text-sm rounded-xl border-primary/40 text-primary hover:bg-primary/10 shadow-xs cursor-pointer"
            onClick={handleDownloadImage}
            disabled={downloading}
          >
            <Download className="h-4 w-4 text-primary" /> Download Certificate Image (PNG)
          </Button>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-9 gap-2 font-bold text-xs rounded-xl border-border text-foreground hover:bg-muted/40 shadow-xs cursor-pointer"
              onClick={() => window.open(linkedinUrl, '_blank')}
            >
              <Globe className="h-3.5 w-3.5 text-primary" /> Share on LinkedIn
            </Button>

            <Button
              variant="outline"
              className="h-9 gap-2 font-bold text-xs rounded-xl border-secondary/40 text-secondary hover:bg-secondary/10 shadow-xs cursor-pointer"
              onClick={handleNativeShareImage}
              disabled={downloading}
            >
              <MessageCircle className="h-3.5 w-3.5 text-secondary" /> WhatsApp
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-9 gap-2 font-bold text-xs rounded-xl border-border text-foreground hover:bg-muted/40 shadow-xs cursor-pointer"
              onClick={() => window.open(twitterUrl, '_blank')}
            >
              <Send className="h-3.5 w-3.5 text-muted-foreground" /> Share on X
            </Button>

            <Button
              variant="outline"
              className="h-9 gap-2 font-bold text-xs rounded-xl border-border text-foreground hover:bg-muted/40 shadow-xs cursor-pointer"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-secondary" /> Copied!
                </>
              ) : (
                <>
                  <Link2 className="h-3.5 w-3.5 text-muted-foreground" /> Copy Text
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
